import { useState, useEffect, useCallback } from 'react';
import {
  Link2, Unlink, Plus, RefreshCw, ArrowLeft,
  MessageSquare, Phone, Mail, Globe, Hash, Smartphone,
  CheckCircle,
} from 'lucide-react';
import { useReplica } from '../contexts/ReplicaContext';
import { api } from '../lib/api';

// Discord logo SVG (lucide doesn't have it)
const DiscordIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.004.044.03.086.058.116a19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

interface IdentityLink {
  id: string;
  replicaId: string;
  channel: string;
  externalId: string;
  displayName?: string;
  verified: boolean;
  linkedAt: string;
  lastActiveAt?: string;
  metadata?: Record<string, unknown>;
}

interface ChannelInfo {
  channel: string;
  label: string;
  description: string;
}

const channelIcons: Record<string, typeof Mail> = {
  email: Mail,
  slack: Hash,
  sms: Phone,
  whatsapp: Smartphone,
  web: Globe,
  telegram: MessageSquare,
};

const DISCORD_COLOR = '#5865F2';

interface IdentityLinksPanelProps {
  replicaId: string;
  onBack: () => void;
}

export default function IdentityLinksPanel({ replicaId, onBack }: IdentityLinksPanelProps) {
  const { getReplicaById } = useReplica();
  const [links, setLinks] = useState<IdentityLink[]>([]);
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [presence, setPresence] = useState<any>(null);
  const [discordOAuthError, setDiscordOAuthError] = useState<string | null>(null);
  const [discordConnecting, setDiscordConnecting] = useState(false);

  // Form state
  const [formChannel, setFormChannel] = useState('');
  const [formExternalId, setFormExternalId] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const replica = getReplicaById(replicaId);
  const replicaName = replica?.name || 'Replica';

  const fetchLinks = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; links: IdentityLink[] }>(`/identity/${encodeURIComponent(replicaId)}`);
      if (res.data?.success) setLinks(res.data.links || []);
    } catch (err) {
      console.error('Failed to fetch identity links:', err);
    } finally {
      setLoading(false);
    }
  }, [replicaId]);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; channels: ChannelInfo[] }>('/identity/channels');
      if (res.data?.success) {
        setChannels(res.data.channels || []);
        if (res.data.channels?.length > 0) setFormChannel(res.data.channels[0].channel);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    }
  }, []);

  const fetchPresence = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; channels: any }>(`/identity/${encodeURIComponent(replicaId)}/presence`);
      if (res.data?.success) setPresence(res.data);
    } catch (err) {
      // ignore
    }
  }, [replicaId]);

  const handleDiscordCallback = useCallback(async (code: string) => {
    setDiscordConnecting(true);
    try {
      const res = await api.post<{ success: boolean }>('/discord/oauth/callback', { code, replicaId });
      if (res.data?.success) {
        fetchLinks();
        fetchPresence();
      } else {
        setDiscordOAuthError(res.error || 'Discord connection failed');
      }
    } catch {
      setDiscordOAuthError('Discord connection failed');
    } finally {
      setDiscordConnecting(false);
    }
  }, [replicaId, fetchLinks, fetchPresence]);

  const handleDiscordConnect = async () => {
    setDiscordOAuthError(null);
    const res = await api.get<{ url: string }>('/discord/oauth/url', { replicaId });
    if (res.data?.url) {
      window.location.href = res.data.url;
    } else {
      setDiscordOAuthError(res.error || 'Could not get Discord OAuth URL');
    }
  };

  useEffect(() => {
    fetchLinks();
    fetchChannels();
    fetchPresence();
  }, [fetchLinks, fetchChannels, fetchPresence]);

  // Handle Discord OAuth redirect callback (?code=xxx&state=replicaId)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (code && state === replicaId) {
      window.history.replaceState({}, '', window.location.pathname);
      handleDiscordCallback(code);
    }
  }, [replicaId, handleDiscordCallback]);

  const handleLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formChannel || !formExternalId) return;
    setSubmitting(true);
    try {
      const res = await api.post<{ success: boolean }>('/identity/link', {
        replicaId,
        channel: formChannel,
        externalId: formExternalId,
        displayName: formDisplayName || undefined,
      });
      if (res.data?.success) {
        setShowForm(false);
        setFormExternalId('');
        setFormDisplayName('');
        fetchLinks();
        fetchPresence();
      }
    } catch (err) {
      console.error('Failed to link identity:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlink = async (linkId: string) => {
    try {
      await api.delete(`/identity/${linkId}`);
      setLinks(links.filter(l => l.id !== linkId));
      fetchPresence();
    } catch (err) {
      console.error('Failed to unlink identity:', err);
    }
  };

  const channelLabel = (ch: string) => {
    return channels.find(c => c.channel === ch)?.label || ch;
  };

  const ChannelIcon = ({ channel, size = 16 }: { channel: string; size?: number }) => {
    if (channel === 'discord') return <DiscordIcon size={size} />;
    const Icon = channelIcons[channel] || Globe;
    return <Icon size={size} />;
  };

  const discordLink = links.find(l => l.channel === 'discord');

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-hig-text-primary">Identity Links</h1>
          <p className="text-sm text-hig-text-secondary">
            Connect <span className="text-hig-accent">{replicaName}</span> to external channels
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-hig-accent text-white rounded-xl text-sm font-medium hover:bg-hig-accent/90 transition-colors"
        >
          <Plus size={16} />
          Link Channel
        </button>
      </div>

      {/* Channel Status Hub (M6) */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-hig-divider">
          <p className="text-xs text-hig-text-tertiary uppercase tracking-wider mb-1">Multi-Channel Gateway</p>
          <p className="text-[11px] text-hig-text-tertiary">Manage messaging channels and see format previews</p>
        </div>
        <div className="p-4 space-y-3">
          {/* Channel Cards */}
          {['telegram', 'whatsapp', 'slack', 'sms', 'email', 'web'].map((ch) => {
            const isActive = presence?.channels?.[ch]?.active || links.some(l => l.channel === ch && l.verified);
            const linkedCount = links.filter(l => l.channel === ch).length;
            const formatPreview: Record<string, string> = {
              telegram: '<b>Bold</b> <i>italic</i> <code>code</code>',
              slack: '*Bold* _italic_ `code`',
              whatsapp: '*Bold* _italic_ ```code```',
              sms: 'Bold italic code (plain text)',
              email: '<strong>Bold</strong> <em>italic</em> <code>code</code>',
              web: '**Bold** *italic* `code`',
            };

            return (
              <div key={ch} className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isActive ? 'bg-emerald-500/5 border border-emerald-500/10' : 'bg-hig-bg-tertiary/30'}`}>
                <div className={`p-2 rounded-lg ${isActive ? 'bg-emerald-500/10 text-emerald-500' : 'bg-hig-bg-tertiary text-hig-text-tertiary'}`}>
                  <ChannelIcon channel={ch} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-hig-text-primary capitalize">{channelLabel(ch)}</p>
                    {isActive && (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    )}
                    {linkedCount > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-hig-accent/10 text-hig-accent">
                        {linkedCount} linked
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-hig-text-tertiary font-mono mt-0.5 truncate" title={formatPreview[ch]}>
                    fmt: {formatPreview[ch] || 'default'}
                  </p>
                </div>
                <div className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-hig-bg-tertiary text-hig-text-tertiary'}`}>
                  {isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Discord Connection Card */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: `${DISCORD_COLOR}1a`, color: DISCORD_COLOR }}>
            <DiscordIcon size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-hig-text-primary">Discord</p>
            {discordLink ? (
              <div className="flex items-center gap-1.5 mt-0.5">
                <CheckCircle size={13} className="text-green-500" />
                <span className="text-xs text-green-500 font-medium">
                  {discordLink.displayName
                    ? `${discordLink.displayName} (${discordLink.externalId})`
                    : discordLink.externalId}
                </span>
              </div>
            ) : (
              <p className="text-xs text-hig-text-tertiary">Not connected</p>
            )}
          </div>
          {discordLink ? (
            <button
              onClick={() => handleUnlink(discordLink.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-hig-error/70 hover:text-hig-error hover:bg-hig-error/10 rounded-lg text-sm transition-colors"
            >
              <Unlink size={14} />
              Disconnect
            </button>
          ) : (
            <button
              onClick={handleDiscordConnect}
              disabled={discordConnecting}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              style={{ background: DISCORD_COLOR }}
            >
              <DiscordIcon size={14} />
              {discordConnecting ? 'Connecting...' : 'Connect Discord'}
            </button>
          )}
        </div>
        {discordOAuthError && (
          <p className="mt-2 text-xs text-hig-error">{discordOAuthError}</p>
        )}
      </div>

      {/* Link Form */}
      {showForm && (
        <form onSubmit={handleLink} className="card p-5 space-y-4 border-2 border-hig-accent/20">
          <p className="text-sm font-medium text-hig-text-primary">Link a new channel identity</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-hig-text-secondary mb-1">Channel</label>
              <div className="flex gap-2 flex-wrap">
                {channels.map((ch) => (
                  <button
                    key={ch.channel}
                    type="button"
                    onClick={() => setFormChannel(ch.channel)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${formChannel === ch.channel
                        ? 'bg-hig-accent text-white'
                        : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'
                      }`}
                  >
                    <ChannelIcon channel={ch.channel} />
                    {ch.label}
                  </button>
                ))}
                {channels.length === 0 && (
                  <div className="flex gap-2">
                    {['email', 'slack', 'sms', 'whatsapp', 'web', 'telegram'].map((ch) => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setFormChannel(ch)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${formChannel === ch
                            ? 'bg-hig-accent text-white'
                            : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'
                          }`}
                      >
                        <ChannelIcon channel={ch} />
                        <span className="capitalize">{ch}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-hig-text-secondary mb-1">External ID</label>
              <input
                type="text"
                value={formExternalId}
                onChange={(e) => setFormExternalId(e.target.value)}
                placeholder={formChannel === 'email' ? 'user@example.com' : formChannel === 'slack' ? 'U0123ABCDEF' : 'ID or handle'}
                className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-hig-text-secondary mb-1">Display Name (optional)</label>
              <input
                type="text"
                value={formDisplayName}
                onChange={(e) => setFormDisplayName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-3 py-2 bg-hig-bg-tertiary border border-hig-divider rounded-lg text-sm text-hig-text-primary focus:outline-none focus:border-hig-accent"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-hig-text-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !formChannel || !formExternalId}
              className="px-5 py-2 bg-hig-accent text-white rounded-lg text-sm font-medium hover:bg-hig-accent/90 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Linking...' : 'Link Identity'}
            </button>
          </div>
        </form>
      )}

      {/* Links List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={20} className="animate-spin text-hig-text-tertiary" />
        </div>
      ) : links.length === 0 ? (
        <div className="card p-8 text-center">
          <Link2 size={32} className="mx-auto text-hig-text-tertiary mb-3" />
          <p className="text-hig-text-secondary font-medium">No channels linked yet</p>
          <p className="text-hig-text-tertiary text-xs mt-1">
            Link external channels so your Replica can be reached via Slack, SMS, email, and more
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <div key={link.id} className="card p-4 flex items-center gap-4">
              <div className={`p-2 rounded-lg ${link.verified ? 'bg-green-500/10 text-green-500' : 'bg-hig-bg-tertiary text-hig-text-tertiary'}`}>
                <ChannelIcon channel={link.channel} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-hig-text-primary capitalize">{channelLabel(link.channel)}</p>
                  {link.verified && (
                    <span className="px-1.5 py-0.5 bg-green-500/10 text-green-500 text-xs rounded-full">Verified</span>
                  )}
                </div>
                <p className="text-xs text-hig-text-secondary">
                  {link.displayName ? `${link.displayName} (${link.externalId})` : link.externalId}
                </p>
                {link.lastActiveAt && (
                  <p className="text-xs text-hig-text-tertiary mt-0.5">
                    Last active: {new Date(link.lastActiveAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleUnlink(link.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-hig-error/70 hover:text-hig-error hover:bg-hig-error/10 rounded-lg text-sm transition-colors"
                title="Unlink this identity"
              >
                <Unlink size={14} />
                Unlink
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
